import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation, useParams } from 'react-router';
import HashLink from '../common/lib/HashLink';
import PageNotFound from '../common/lib/PageNotFound';
import type { MultiLang } from '../config';
import Functions from '../functions';
import D3ForumUtils, { D3ForumPostSortOrder } from './lib/D3ForumUtils';
import ForumJump from './lib/ForumJump';
import Post from './lib/Post';
import PostsTree from './lib/PostsTree';

interface Props {
    lang: MultiLang;
    name: string;
}

const D3ForumTopic = (props: Props) => {
    const { lang, name } = props;
    const { search } = useLocation();
    const { id } = useParams<{ id?: string }>();
    const topicId = typeof id === 'undefined' ? 0 : Number.parseInt(id, 10);
    const orderParam = new URLSearchParams(search).get('order');
    const order =
        orderParam !== null && orderParam.match(/(0|1|2)/) !== null
            ? (Number.parseInt(orderParam, 10) as D3ForumPostSortOrder)
            : D3ForumPostSortOrder.TREE;

    const topic = D3ForumUtils.getTopic(name, topicId);
    const forum = D3ForumUtils.getForum(name, topic === null ? 0 : topic.forum_id);
    const category = D3ForumUtils.getCategory(name, forum === null ? 0 : forum.cat_id);
    if (topic === null || forum === null || category === null) {
        return <PageNotFound lang={lang} />;
    }
    const posts = D3ForumUtils.getPosts(name, topicId, order);

    const orderCtrl = (
        <div className="clearfix">
            <div className="d3f_order_ctrl">
                {order !== D3ForumPostSortOrder.TREE && (
                    <HashLink to={D3ForumUtils.getTopicUrl(name, topic.topic_id, D3ForumPostSortOrder.TREE, null)}>
                        {Functions.mlang('[en]Tree order[/en][ja]ツリー構造順で表示[/ja]', lang)}
                    </HashLink>
                )}
                {order !== D3ForumPostSortOrder.OLD && (
                    <HashLink to={D3ForumUtils.getTopicUrl(name, topic.topic_id, D3ForumPostSortOrder.OLD, null)}>
                        {Functions.mlang('[en]Older is upper[/en][ja]投稿の古いものから[/ja]', lang)}
                    </HashLink>
                )}
                {order !== D3ForumPostSortOrder.NEW && (
                    <HashLink to={D3ForumUtils.getTopicUrl(name, topic.topic_id, D3ForumPostSortOrder.NEW, null)}>
                        {Functions.mlang('[en]Newer is upper[/en][ja]投稿の新しいものから[/ja]', lang)}
                    </HashLink>
                )}
            </div>
        </div>
    );
    const title = Functions.mlang(topic.topic_title, lang);
    return (
        <>
            <Helmet>
                <title>
                    {title} - {D3ForumUtils.getTitle(name, lang)} - {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            <div className="d3f_breadcrumbs">
                <HashLink to={D3ForumUtils.getIndexUrl(name)}>
                    {Functions.mlang('[en]Top[/en][ja]トップ[/ja]', lang)}
                </HashLink>
                &nbsp;&gt;&nbsp;
                <HashLink to={D3ForumUtils.getCategoryUrl(name, category.cat_id)}>
                    {Functions.mlang(category.cat_title, lang)}
                </HashLink>
                &nbsp;&gt;&nbsp;
                <HashLink to={D3ForumUtils.getForumUrl(name, forum.forum_id)}>
                    {Functions.mlang(forum.forum_title, lang)}
                </HashLink>
            </div>
            <h1 className="d3f_title">{title}</h1>
            <PostsTree lang={lang} name={name} posts={posts} postId={0} />
            {orderCtrl}
            <div className="d3f_wrap">
                {posts.map((post, idx) => {
                    const prevPostId = idx === 0 ? 0 : posts[idx - 1].post_id;
                    const nextPostId = idx + 1 === posts.length ? 0 : posts[idx + 1].post_id;
                    return (
                        <Post
                            key={post.post_id}
                            lang={lang}
                            name={name}
                            post={post}
                            prevPostId={prevPostId}
                            nextPostId={nextPostId}
                            inTopic={true}
                        />
                    );
                })}
            </div>
            {orderCtrl}
            <ForumJump lang={lang} name={name} forumId={forum.forum_id} />
        </>
    );
};

export default D3ForumTopic;
